// Путь: D:\QR\QR-WEB\dashboard\src\components\User\MyPromocodes.jsx
// Название: My Promocodes - Professional Redesign
// Версия: 4.0.0
// Дата: 06.02.2026

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

const API_URL = 'https://qr-money.onrender.com/api';

const MyPromocodes = () => {
  const [promocodes, setPromocodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [copiedId, setCopiedId] = useState(null);
  const [filter, setFilter] = useState('all');
  const navigate = useNavigate();

  useEffect(() => {
    loadPromocodes();
  }, []);

  const loadPromocodes = async () => {
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(`${API_URL}/promocodes/my`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      if (data.success) {
        setPromocodes(data.data || []);
      }
    } catch (error) {
      console.error('Load promocodes error:', error);
    } finally {
      setLoading(false);
    }
  };

  const copyCode = (code, index) => {
    navigator.clipboard.writeText(code);
    setCopiedId(index);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const formatDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('ru-RU', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const filteredPromocodes = promocodes.filter(p => {
    if (filter === 'all') return true;
    return p.type === filter;
  });

  const stats = {
    total: promocodes.length,
    main: promocodes.filter(p => p.type === 'main').length,
    referral: promocodes.filter(p => p.type === 'referral').length,
    totalDar: promocodes.reduce((sum, p) => sum + parseFloat(p.dar_amount || 0), 0)
  };

  // ============================================
  // LOADING STATE
  // ============================================
  if (loading) {
    return (
      <div style={s.page}>
        <div style={s.loadingWrap}>
          <div style={s.loadingPulse}></div>
          <p style={s.loadingText}>Загружаем промокоды...</p>
        </div>
      </div>
    );
  }

  // ============================================
  // RENDER
  // ============================================
  return (
    <div style={s.page}>

      {/* ===== HERO HEADER ===== */}
      <div style={s.hero}>
        <div style={s.heroContent}>
          <div style={s.heroLeft}>
            <h1 style={s.heroTitle}>Мои промокоды</h1>
            <p style={s.heroSub}>Скопируйте код и используйте на сайте бренда</p>
          </div>
          <button style={s.refreshBtn} onClick={loadPromocodes}>
            <span style={s.refreshIcon}>↻</span> Обновить
          </button>
        </div>

        {/* Stats Row */}
        <div style={s.statsRow}>
          {[
            { value: stats.total, label: 'Всего', color: '#c4b5fd', bg: 'rgba(139,92,246,0.15)' },
            { value: stats.main, label: 'Прямых', color: '#6ee7b7', bg: 'rgba(16,185,129,0.15)' },
            { value: stats.referral, label: 'Реферальных', color: '#93c5fd', bg: 'rgba(59,130,246,0.15)' },
            { value: stats.totalDar.toFixed(0), label: 'DAR потрачено', color: '#fcd34d', bg: 'rgba(245,158,11,0.15)' },
          ].map((stat, i) => (
            <div key={i} style={{...s.statPill, background: stat.bg}}>
              <span style={{...s.statNum, color: stat.color}}>{stat.value}</span>
              <span style={s.statLabel}>{stat.label}</span>
            </div>
          ))}
        </div>
      </div>

      {/* ===== FILTER TABS ===== */}
      <div style={s.filterBar}>
        {[
          { key: 'all', label: `Все (${stats.total})` },
          { key: 'main', label: `🎁 Прямые (${stats.main})` },
          { key: 'referral', label: `👥 Реферальные (${stats.referral})` },
        ].map(tab => (
          <button
            key={tab.key}
            style={{...s.filterBtn, ...(filter === tab.key ? s.filterActive : {})}}
            onClick={() => setFilter(tab.key)}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {/* ===== EMPTY STATE ===== */}
      {filteredPromocodes.length === 0 ? (
        <div style={s.empty}>
          <div style={s.emptyCircle}>
            <span style={s.emptyEmoji}>🎟️</span>
          </div>
          <h3 style={s.emptyTitle}>Пока нет промокодов</h3>
          <p style={s.emptyDesc}>Активируйте подарки и обменивайте DAR на скидки у брендов</p>
          <button style={s.emptyBtn} onClick={() => navigate('/brand-store')}>
            Перейти в Brand Store →
          </button>
        </div>
      ) : (
        /* ===== PROMOCODES LIST ===== */
        <div style={s.list}>
          {filteredPromocodes.map((promo, index) => {
            const isCopied = copiedId === index;
            const isReferral = promo.type === 'referral';
            
            return (
              <div key={index} style={s.card} className="promo-card">
                {/* Type indicator stripe */}
                <div style={{
                  ...s.cardStripe,
                  background: isReferral 
                    ? 'linear-gradient(180deg, #3B82F6, #2563EB)' 
                    : 'linear-gradient(180deg, #8B5CF6, #7C3AED)'
                }} />
                
                <div style={s.cardBody}>
                  {/* Top row: brand + dar amount */}
                  <div style={s.cardTop}>
                    <div style={s.brandRow}>
                      <div style={{
                        ...s.brandDot,
                        background: isReferral 
                          ? 'linear-gradient(135deg, #3B82F6, #2563EB)' 
                          : 'linear-gradient(135deg, #8B5CF6, #7C3AED)'
                      }}>
                        {promo.logo_emoji || '🎁'}
                      </div>
                      <div>
                        <div style={s.brandName}>{promo.brand_name || promo.campaign_name || 'Бренд'}</div>
                        <div style={s.typeTag}>
                          {isReferral ? '👥 Реферальный' : '🎁 Прямой'}
                        </div>
                      </div>
                    </div>
                    <div style={s.darChip}>
                      −{parseFloat(promo.dar_amount).toFixed(0)} DAR
                    </div>
                  </div>

                  {/* Code block */}
                  <div style={s.codeRow}>
                    <div 
                      style={{...s.codeWrap, ...(isCopied ? s.codeWrapCopied : {})}}
                      onClick={() => copyCode(promo.code, index)}
                      title="Нажмите чтобы скопировать"
                    >
                      <code style={s.code}>{promo.code}</code>
                      <span style={{...s.copyHint, ...(isCopied ? s.copyHintActive : {})}}>
                        {isCopied ? '✓ Скопировано!' : 'Нажмите чтобы скопировать'}
                      </span>
                    </div>
                  </div>

                  {/* Bottom row: date + shop link */}
                  <div style={s.cardBottom}>
                    <span style={s.dateText}>Получен: {formatDate(promo.redeemed_at)}</span>
                    {promo.shop_url && (
                      <a
                        href={promo.shop_url.startsWith('http') ? promo.shop_url : `https://${promo.shop_url}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        style={s.shopLink}
                        className="shop-link"
                      >
                        Перейти на сайт <span style={s.arrow}>→</span>
                      </a>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ===== HOW TO USE ===== */}
      {promocodes.length > 0 && (
        <div style={s.howTo}>
          <div style={s.howToTitle}>💡 Как использовать промокод</div>
          <div style={s.stepsRow}>
            {[
              { num: '1', text: 'Скопируйте код' },
              { num: '2', text: 'Откройте сайт бренда' },
              { num: '3', text: 'Введите при оформлении' },
              { num: '4', text: 'Получите скидку!' },
            ].map((step, i) => (
              <div key={i} style={s.stepItem}>
                <div style={s.stepBadge}>{step.num}</div>
                <span style={s.stepText}>{step.text}</span>
                {i < 3 && <span style={s.stepArrow}>›</span>}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================
// СЕКЦИЯ: Стили
// ============================================
const s = {
  // Page
  page: {
    padding: '24px 20px 48px',
    maxWidth: 840,
    margin: '0 auto',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },

  // Loading
  loadingWrap: { textAlign: 'center', padding: '120px 0' },
  loadingPulse: {
    width: 48, height: 48,
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #8B5CF6, #6366F1)',
    margin: '0 auto 20px',
    animation: 'pulse 1.2s ease-in-out infinite',
  },
  loadingText: { fontSize: 15, color: '#9ca3af', letterSpacing: '0.02em' },

  // Hero
  hero: {
    background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 50%, #3730a3 100%)',
    borderRadius: 20,
    padding: '28px 28px 24px',
    marginBottom: 20,
    color: 'white',
  },
  heroContent: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: 24,
  },
  heroLeft: {},
  heroTitle: {
    fontSize: 26,
    fontWeight: 700,
    margin: '0 0 6px',
    letterSpacing: '-0.02em',
  },
  heroSub: {
    fontSize: 14,
    margin: 0,
    opacity: 0.65,
  },
  refreshBtn: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    padding: '8px 16px',
    background: 'rgba(255,255,255,0.1)',
    border: '1px solid rgba(255,255,255,0.18)',
    borderRadius: 10,
    color: 'white',
    fontSize: 13,
    fontWeight: 500,
    cursor: 'pointer',
    transition: 'background 0.2s',
  },
  refreshIcon: { fontSize: 16 },

  // Stats Row
  statsRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(4, 1fr)',
    gap: 10,
  },
  statPill: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    padding: '14px 8px',
    borderRadius: 14,
  },
  statNum: {
    fontSize: 24,
    fontWeight: 800,
    lineHeight: 1,
    marginBottom: 4,
  },
  statLabel: {
    fontSize: 10,
    color: 'rgba(255,255,255,0.55)',
    textTransform: 'uppercase',
    letterSpacing: '0.06em',
    fontWeight: 600,
  },

  // Filter
  filterBar: {
    display: 'flex',
    gap: 6,
    marginBottom: 16,
    padding: 4,
    background: '#f1f5f9',
    borderRadius: 14,
  },
  filterBtn: {
    flex: 1,
    padding: '10px 12px',
    border: 'none',
    borderRadius: 11,
    background: 'transparent',
    color: '#64748b',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  filterActive: {
    background: 'white',
    color: '#1e293b',
    boxShadow: '0 1px 6px rgba(0,0,0,0.08)',
  },

  // Empty
  empty: {
    textAlign: 'center',
    padding: '64px 32px',
    background: 'white',
    borderRadius: 20,
    border: '1px solid #e2e8f0',
  },
  emptyCircle: {
    width: 80, height: 80,
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #f1f5f9, #e2e8f0)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    margin: '0 auto 20px',
  },
  emptyEmoji: { fontSize: 36 },
  emptyTitle: { fontSize: 18, fontWeight: 700, color: '#1e293b', margin: '0 0 8px' },
  emptyDesc: { fontSize: 14, color: '#94a3b8', margin: '0 0 24px', lineHeight: 1.5 },
  emptyBtn: {
    padding: '12px 28px',
    background: 'linear-gradient(135deg, #8B5CF6, #7C3AED)',
    color: 'white',
    border: 'none',
    borderRadius: 12,
    fontSize: 14,
    fontWeight: 600,
    cursor: 'pointer',
  },

  // List
  list: {
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
  },

  // Card
  card: {
    display: 'flex',
    background: 'white',
    borderRadius: 16,
    overflow: 'hidden',
    border: '1px solid #e2e8f0',
    transition: 'box-shadow 0.2s',
  },
  cardStripe: {
    width: 5,
    flexShrink: 0,
  },
  cardBody: {
    flex: 1,
    padding: '16px 20px',
  },

  // Card Top
  cardTop: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 14,
  },
  brandRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 10,
  },
  brandDot: {
    width: 38, height: 38,
    borderRadius: 10,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 18,
  },
  brandName: {
    fontSize: 15,
    fontWeight: 700,
    color: '#1e293b',
    lineHeight: 1.2,
  },
  typeTag: {
    fontSize: 12,
    color: '#94a3b8',
    marginTop: 2,
  },
  darChip: {
    padding: '5px 10px',
    background: '#fef2f2',
    color: '#ef4444',
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 700,
  },

  // Code Row
  codeRow: {
    marginBottom: 14,
  },
  codeWrap: {
    background: '#f8fafc',
    border: '1.5px dashed #cbd5e1',
    borderRadius: 12,
    padding: '14px 20px 10px',
    textAlign: 'center',
    cursor: 'pointer',
    transition: 'all 0.2s',
  },
  codeWrapCopied: {
    background: '#f0fdf4',
    borderColor: '#86efac',
  },
  code: {
    fontFamily: '"SF Mono", "Fira Code", "Consolas", monospace',
    fontSize: 17,
    fontWeight: 700,
    color: '#334155',
    letterSpacing: '2px',
    display: 'block',
    marginBottom: 6,
    userSelect: 'all',
  },
  copyHint: {
    fontSize: 11,
    color: '#94a3b8',
    transition: 'color 0.2s',
  },
  copyHintActive: {
    color: '#16a34a',
    fontWeight: 600,
  },

  // Card Bottom
  cardBottom: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingTop: 12,
    borderTop: '1px solid #f1f5f9',
  },
  dateText: {
    fontSize: 12,
    color: '#94a3b8',
  },
  shopLink: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 4,
    fontSize: 13,
    fontWeight: 600,
    color: '#7C3AED',
    textDecoration: 'none',
    padding: '6px 14px',
    background: '#f5f3ff',
    borderRadius: 8,
    transition: 'all 0.2s',
  },
  arrow: { fontSize: 14 },

  // How To
  howTo: {
    marginTop: 20,
    padding: '20px 24px',
    background: '#fffbeb',
    borderRadius: 16,
    border: '1px solid #fde68a',
  },
  howToTitle: {
    fontSize: 14,
    fontWeight: 700,
    color: '#92400e',
    marginBottom: 16,
  },
  stepsRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 6,
    flexWrap: 'wrap',
  },
  stepItem: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  stepBadge: {
    width: 24, height: 24,
    borderRadius: '50%',
    background: '#f59e0b',
    color: 'white',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 12,
    fontWeight: 700,
    flexShrink: 0,
  },
  stepText: {
    fontSize: 13,
    color: '#78350f',
    whiteSpace: 'nowrap',
  },
  stepArrow: {
    fontSize: 18,
    color: '#d97706',
    margin: '0 4px',
  },
};

// ============================================
// СЕКЦИЯ: CSS анимации + hover эффекты
// ============================================
const styleTag = document.createElement('style');
styleTag.textContent = `
  @keyframes pulse {
    0%, 100% { transform: scale(1); opacity: 1; }
    50% { transform: scale(1.08); opacity: 0.7; }
  }
  .promo-card:hover {
    box-shadow: 0 4px 20px rgba(0,0,0,0.08) !important;
  }
  .shop-link:hover {
    background: #ede9fe !important;
    transform: translateX(2px);
  }
`;
document.head.appendChild(styleTag);

export default MyPromocodes;